import { useSenderAccountContext } from "context/SenderAccountContext";
import {
  ACCOUNT_ROLE_ADMIN_TRANSLATIONS,
  ACCOUNT_ROLE_CARESEEKER_USER_MANAGEMENT,
  ACCOUNT_ROLE_CLINICAL_DOCUMENTATION,
  ACCOUNT_ROLE_DTA_APPROVER,
  ACCOUNT_ROLE_PREDICT,
  ACCOUNT_ROLE_RECEIVER_ADMISSIONS,
  ACCOUNT_ROLE_RECEIVER_ANALYTICS,
  ACCOUNT_ROLE_REPORT,
  ACCOUNT_ROLE_SENDER_IT,
  ACCOUNT_STATUS_ACTIVE,
  ACCOUNT_STATUS_CREATED,
  ACCOUNT_STATUS_INACTIVE,
  ACCOUNT_TYPE_EXTERNAL,
  ACCOUNT_TYPE_PROVIDER_SEARCH,
  ACCOUNT_TYPE_STAFF,
  ACCOUNT_TYPE_TOOL,
  CREATE_PATIENT_ROLES,
  FACILITY_COMPANIONSHIP,
  FACILITY_MOBILE_CARE,
  FACILITY_SHORT_TERM_CARE,
  FACILITY_STATIC_CARE,
  PRODUCT_FEATURES,
} from "core/consts";
import { getProductFeatureActive } from "core/model/productfeatures";
import { isProd } from "core/model/utils/featureFlags";
import {
  Account,
  AccountRole,
  Careprovider,
  GetOntologyType,
  ProductFeature,
} from "core/types";
import { getUnixTime } from "date-fns";
import {
  getDefaultProviderAccountId,
  selectCareseekers,
  selectCredentials,
  selectIdentification,
} from "reduxentities/selectors";
import { AuthState } from "reduxentities/slices/authSlice";
import { RootState } from "reduxentities/store";
import Translations from "translations/types";
import { hasAnyFacility } from "../auctions";
import { ERROR_COLOR, SUCCESS_COLOR } from "../utils/colors";

export type GetNameOptions = {
  providerSearchAccountName?: string;
  withAcademicTitle?: boolean;
  withFirstInitial?: boolean;
  withSalutation?: boolean;
};

export const TOKEN_STATUS = {
  EXPIRED: "expired",
  NO_TOKEN: "noToken",
  VALID: "valid",
} as const;

const defaultOptions = {
  withAcademicTitle: true,
  withSalutation: true,
} as const;

const isNumber = (v: any) => typeof v === "number";

export function getSalutation({
  gender,
  getOntology,
  salutation,
  title,
}: {
  gender?: number;
  getOntology: GetOntologyType;
  salutation?: number;
  title?: number;
}): string | null {
  if (title != null && isNumber(title))
    return getOntology({ key: title, type: "salutation" });
  if (gender != null && isNumber(gender))
    return getOntology({ key: gender, type: "salutation" });
  if (salutation != null && isNumber(salutation))
    return getOntology({ key: salutation, type: "salutation" });

  return null;
}

function hasAccessWithStaffOverride(
  account: Account | null | undefined,
  activeRoles: Array<number> | undefined,
  hasRequiredRole: boolean,
): boolean {
  if (!account) return false;
  if (account.account_type === ACCOUNT_TYPE_STAFF) return true;
  if (!activeRoles) return false;

  return hasRequiredRole;
}

export function hasAccessToUserManagement(
  account: Account | null | undefined,
  activeRoles: Array<number> | undefined,
) {
  const hasRequiredRole = !!activeRoles?.some(
    (role) => role === ACCOUNT_ROLE_CARESEEKER_USER_MANAGEMENT,
  );
  return hasAccessWithStaffOverride(account, activeRoles, hasRequiredRole);
}

export function hasAccessToAnalytics(
  account: Account | null | undefined,
  careprovider: Careprovider | undefined,
): boolean {
  if (!account || !careprovider) return false;

  // DEV-14596 feature flag. Remove when activating
  if (isProd && !isStaffAccount(account)) {
    return false;
  }
  // End

  if (
    !getProductFeatureActive(
      careprovider.product_features,
      PRODUCT_FEATURES.ANALYTICS,
    )
  ) {
    return false;
  }

  if (isStaffAccount(account)) {
    return true;
  }

  return hasCareproviderRole(
    account,
    careprovider?.id,
    ACCOUNT_ROLE_RECEIVER_ANALYTICS,
  );
}

export function hasAccessToAdmissions(
  account: Account | null | undefined,
  careprovider: Careprovider | undefined,
) {
  if (!account || !careprovider) {
    return false;
  }

  const isAdmissionsFeatureActive = getProductFeatureActive(
    careprovider.product_features,
    PRODUCT_FEATURES.ADMISSIONS,
  );

  const isMobileOrStaticProvider = hasAnyFacility(careprovider.facilities, [
    FACILITY_MOBILE_CARE,
    FACILITY_COMPANIONSHIP,
    FACILITY_STATIC_CARE,
    FACILITY_SHORT_TERM_CARE,
  ]);

  const hasAdmissionsRole =
    hasCareproviderRole(
      account,
      careprovider?.id,
      ACCOUNT_ROLE_RECEIVER_ADMISSIONS,
    ) || isStaffAccount(account);

  return (
    hasAdmissionsRole && isAdmissionsFeatureActive && isMobileOrStaticProvider
  );
}

export function hasAccessToOnPremDebug(
  account: Account | null | undefined,
  activeRoles: Array<number> | undefined,
) {
  const hasRequiredRole = !!activeRoles?.includes(ACCOUNT_ROLE_SENDER_IT);
  return hasAccessWithStaffOverride(account, activeRoles, hasRequiredRole);
}

export function hasAccessToReport(
  account: Account | null | undefined,
  activeRoles: Array<number> | undefined,
) {
  const hasRequiredRole = !!activeRoles?.includes(ACCOUNT_ROLE_REPORT);
  return hasAccessWithStaffOverride(account, activeRoles, hasRequiredRole);
}

export function hasAccessToAiChat(
  product_features: ProductFeature[] | undefined,
): boolean {
  return (
    !isProd &&
    getProductFeatureActive(product_features, PRODUCT_FEATURES.AI_CHAT)
  );
}

export function useHasAccessToAiChat(): boolean {
  const { careseeker } = useSenderAccountContext();
  return hasAccessToAiChat(careseeker?.product_features);
}

export function hasAccessToDTAApproval(
  account: Account | null | undefined,
  activeRoles: Array<number> | undefined,
) {
  const hasRequiredRole = !!activeRoles?.includes(ACCOUNT_ROLE_DTA_APPROVER);
  return hasAccessWithStaffOverride(account, activeRoles, hasRequiredRole);
}

export function hasAccessToPredict(
  account: Account | null | undefined,
  activeRoles: Array<number> | undefined,
  product_features: ProductFeature[] | undefined,
) {
  const hasRequiredRole =
    getProductFeatureActive(product_features, PRODUCT_FEATURES.PREDICT) &&
    !!activeRoles?.includes(ACCOUNT_ROLE_PREDICT);
  return hasAccessWithStaffOverride(account, activeRoles, hasRequiredRole);
}

export function hasAccessToPatientDashboard(
  account: Account | null | undefined,
  activeRoles: Array<number> | undefined,
) {
  const dashboardAllowedRoles = CREATE_PATIENT_ROLES;
  const hasRequiredRole = !!activeRoles?.some((activeRole) =>
    dashboardAllowedRoles.includes(
      activeRole as (typeof dashboardAllowedRoles)[number],
    ),
  );
  return hasAccessWithStaffOverride(account, activeRoles, hasRequiredRole);
}

/**
 * Clinical documentation (doctor) dashboard access. Role-only: requires
 * ACCOUNT_ROLE_CLINICAL_DOCUMENTATION and no create-patient roles. No staff override.
 */
export function hasAccessToDoctorDashboard(
  activeRoles: Array<number> | undefined,
) {
  const hasDoctorRole = !!activeRoles?.includes(
    ACCOUNT_ROLE_CLINICAL_DOCUMENTATION,
  );
  const hasPatientCreateRoles = !!activeRoles?.some((activeRole) =>
    CREATE_PATIENT_ROLES.includes(
      activeRole as (typeof CREATE_PATIENT_ROLES)[number],
    ),
  );
  return hasDoctorRole && !hasPatientCreateRoles;
}

export function hasCareseekerRole(
  account: Account | undefined,
  careseekerId: number | undefined,
  roleId: AccountRole,
) {
  if (!account) return false;

  const careseekerRoles = account.roles?.careseeker_roles?.find(
    (entry) => entry.careseeker?.id === careseekerId,
  );
  if (!careseekerRoles) return false;

  return careseekerRoles.roles?.includes(roleId);
}

export function hasCareproviderRole(
  account: Account | null | undefined,
  careproviderId: number | undefined,
  roleId: AccountRole,
): boolean {
  if (!account || !careproviderId) return false;

  const filteredRoles = account.roles?.careprovider_roles?.find(
    (entry) => entry.careprovider?.id === careproviderId,
  );

  return !!filteredRoles?.roles?.includes(roleId);
}

export function isUserManager(
  account: Account | undefined,
  careseekerId: number | undefined,
) {
  return hasCareseekerRole(
    account,
    careseekerId,
    ACCOUNT_ROLE_CARESEEKER_USER_MANAGEMENT,
  );
}

export function isActiveAccount(account: Account) {
  return account.status === ACCOUNT_STATUS_ACTIVE;
}

export function isStaffAccount(account: Account | undefined) {
  return account?.account_type === ACCOUNT_TYPE_STAFF;
}

export function isExternalAccount(account: Account | undefined) {
  return account?.account_type === ACCOUNT_TYPE_EXTERNAL;
}

export function getAccountStatusColor(
  status: number | null | undefined,
): string | undefined {
  switch (status) {
    case ACCOUNT_STATUS_CREATED:
      return undefined;
    case ACCOUNT_STATUS_INACTIVE:
      return ERROR_COLOR;
    case ACCOUNT_STATUS_ACTIVE:
      return SUCCESS_COLOR;
    default:
      return undefined;
  }
}

// todo: add this in BE in the event context
export const getProviderSearchAccountName = ({
  patientId,
  translations,
}: {
  patientId: string | undefined;
  translations: Translations;
}) => `${translations.auctionRequest.bcpProfileName} ${patientId ?? ""}`;

function getProviderSearchName(
  account: Account,
  options: GetNameOptions,
): string | null {
  if (account.account_type !== ACCOUNT_TYPE_PROVIDER_SEARCH) return null;
  if (!options?.providerSearchAccountName) {
    console.error("no provider search account name provided");
    return "";
  }
  return options.providerSearchAccountName;
}

function buildAdornments(
  account: Account,
  getOntology: GetOntologyType,
  withSalutation: boolean,
  withAcademicTitle: boolean,
): string[] {
  const adornments: string[] = [];

  if (withSalutation) {
    const salutation = getSalutation({ ...account, getOntology });
    if (salutation) adornments.push(salutation);
  }

  if (
    withAcademicTitle &&
    account.academic_title != null &&
    account.academic_title > 0
  ) {
    const title = getOntology({
      key: account.academic_title,
      type: "academicTitle",
    });
    adornments.push(title);
  }

  return adornments;
}

export function getName(
  account: Account | null | undefined,
  getOntology: GetOntologyType,
  options: GetNameOptions = defaultOptions,
) {
  if (!account) return "";
  if (account.account_type === ACCOUNT_TYPE_TOOL) return "";

  const providerSearchName = getProviderSearchName(account, options);
  if (providerSearchName !== null) return providerSearchName;

  if (account.id === getDefaultProviderAccountId()) return "";
  if (!account.first_name && !account.last_name) return "";

  const normalizedOptions = { ...defaultOptions, ...options };
  const { withAcademicTitle, withFirstInitial, withSalutation } =
    normalizedOptions;

  let firstName = "";
  if (account.first_name) {
    if (withFirstInitial) {
      firstName = `${account.first_name[0].toUpperCase()}.`;
    } else {
      firstName = account.first_name;
    }
  }
  const lastName = account.last_name ?? "";
  const name = `${firstName} ${lastName}`.trim();

  const adornments = buildAdornments(
    account,
    getOntology,
    withSalutation,
    withAcademicTitle,
  );

  const adornmentStr = withFirstInitial
    ? [...new Set(adornments.join(" ").split(" "))].join(" ")
    : adornments.join(" ");

  return `${adornmentStr} ${name}`.trim();
}

export function convertSocialWorker(
  socialWorker: Account | null,
  getOntology: GetOntologyType,
  optionsArg?: GetNameOptions,
) {
  const options =
    optionsArg != null ? { ...defaultOptions, ...optionsArg } : defaultOptions;
  return {
    name: getName(socialWorker, getOntology, options),
    originalName: getName(socialWorker, getOntology, {
      withSalutation: false,
    }),
    value: socialWorker?.id,
  };
}

export const convertSocialWorkers = (
  socialWorkers: Array<Account> | undefined,
  getOntology: GetOntologyType,
  optionsArg?: GetNameOptions,
): Array<{
  name: string;
  originalName: string;
  value: number | null | undefined;
}> => {
  if (!socialWorkers) return [];

  return (
    socialWorkers
      .filter(
        (s) =>
          s.first_name && s.last_name && s.account_type !== ACCOUNT_TYPE_STAFF,
      )
      .map((s) => convertSocialWorker(s, getOntology, optionsArg))
      // Filter out empty names.
      // #TODO: Automated task accounts may require distinct handling strategies.
      .filter((s) => s.name && s.value)
      .sort((a, b) => a.originalName.localeCompare(b.originalName))
  );
};

export const getInitialsForAccount = (account?: {
  first_name?: string;
  last_name?: string;
}) => {
  if (!account) return "";
  if (!account.first_name || !account.last_name) return "";

  return [account.first_name, account.last_name]
    .map((n) => n.charAt(0))
    .join("");
};

export function checkToken(state: RootState) {
  const credentials = selectCredentials(state);
  return checkTokenStatus(credentials);
}

export function checkTokenStatus(credentials: AuthState["credentials"]) {
  if (!credentials?.token) return TOKEN_STATUS.NO_TOKEN;

  if (
    credentials.expiration &&
    credentials.expiration < getUnixTime(new Date())
  ) {
    return TOKEN_STATUS.EXPIRED;
  }

  return TOKEN_STATUS.VALID;
}

export const isAdminLogged = (state: RootState) => {
  const check = checkToken(state);
  if (check !== TOKEN_STATUS.VALID) return check;

  const identification = selectIdentification(state);

  if (identification?.account?.account_type !== ACCOUNT_TYPE_STAFF) {
    return "incorrectAccountType";
  }

  if (!identification?.admin_roles?.length) {
    return "noRoles";
  }

  return "ok";
};

export const isCareseekerLogged = (state: RootState): string => {
  const check = checkToken(state);
  if (check !== TOKEN_STATUS.VALID) return check;
  if (
    state.auth?.identification?.account?.account_type ===
    ACCOUNT_TYPE_PROVIDER_SEARCH
  )
    return "incorrectAccountType";

  if (!selectCareseekers(state)) return "noRoles";

  return "ok";
};

export const isProviderSearchLogged = (state: RootState): string => {
  const check = checkToken(state);
  if (check !== TOKEN_STATUS.VALID) return check;

  if (state.auth?.identification?.account?.id == null) return "noAccount";

  if (
    state.auth?.identification?.account?.account_type !==
    ACCOUNT_TYPE_PROVIDER_SEARCH
  )
    return "incorrectAccountType";

  return "ok";
};

export const isTranslationsAdmin = (account: Account | null | undefined) =>
  account?.roles?.admin_roles?.includes(ACCOUNT_ROLE_ADMIN_TRANSLATIONS);

export function isRecareAccount(account: Account | null | undefined) {
  return account?.account_type === ACCOUNT_TYPE_STAFF;
}

export function getAccountValue(
  account: Account | undefined,
  getOntology: GetOntologyType,
  nameOptions: GetNameOptions,
): number {
  if (!account) return -1;

  const converted = convertSocialWorker(account, getOntology, nameOptions);

  return converted?.value ?? -1;
}

export function isAccountLinkedToSeveralProviders(
  account: Account | undefined,
) {
  return (
    account?.roles?.careprovider_roles?.length &&
    account?.roles?.careprovider_roles?.length > 1
  );
}
